/**
 * Le module **Utilisateurs & rôles** — l'annuaire des agents et la matrice des
 * habilitations (`EX-BO-002`, `EX-BO-004`, écran `B-27`).
 *
 * Maquette : artboard `b_utilisateurs`.
 *
 * ## Ce que ce chantier referme
 *
 * Le module ne portait qu'une **écriture** : `BO_USER_CREATE`, depuis la `V7`.
 * On pouvait créer un agent, et rien ne savait dire lesquels existent ni ce que
 * la matrice leur accorde. Un administrateur technique créait donc des comptes
 * à l'aveugle, et un auditeur ne pouvait pas relire la matrice — qui est
 * pourtant une **pièce d'audit**.
 *
 * Deux routes ont été écrites et deux droits posés par `V61`.
 *
 * ## LA MATRICE EST EN LECTURE, ET C'EST ENTIER
 *
 * Elle se lit, elle ne se modifie pas ici. Rendre 350 cases cliquables sans
 * filet serait un incident en attente ; et surtout, **aucune route ne sert la
 * modification d'une cellule** — l'offrir serait le bouton inerte que ce dépôt
 * refuse ailleurs. Ce qui manque est déclaré plutôt que tu.
 *
 * ## LES CELLULES REFUSÉES SONT RENDUES
 *
 * Un « — » n'est pas un vide : c'est une décision. Omettre les refus ferait
 * confondre « interdit » avec « pas encore décrit », et c'est cette distinction
 * que la barre latérale tient depuis qu'elle a cessé de proposer ce qui échoue
 * toujours.
 *
 * ## L'INFORMATION N'EST JAMAIS PORTÉE PAR LA SEULE COULEUR
 *
 * Les trois régimes se lisent au **texte de remplacement** autant qu'au glyphe :
 * « ✔✔ » a un sens réglementaire — deux signatures —, et un auditeur qui
 * distingue mal deux nuances de gris doit pouvoir le lire.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { fetchAgents, fetchPermissionMatrix } from '../http/administration.js';
import type {
  BackOfficeAgent,
  MatrixAction,
  PermissionMatrix,
} from '../http/administration.js';

/** Le module Utilisateurs & rôles. */
export function UsersRolesScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [agents, setAgents] = useState<readonly BackOfficeAgent[] | null>(null);
  const [erreurAgents, setErreurAgents] = useState<string | null>(null);
  const [matrice, setMatrice] = useState<PermissionMatrix | null>(null);
  const [erreurMatrice, setErreurMatrice] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const [annuaire, grille] = await Promise.all([
        fetchAgents(client).then(
          (liste) => ({ liste, erreur: null as string | null }),
          (echec: unknown) => ({
            liste: null,
            erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
          }),
        ),
        fetchPermissionMatrix(client).then(
          (vue) => ({ vue, erreur: null as string | null }),
          (echec: unknown) => ({
            vue: null,
            erreur: echec instanceof ApiError ? echec.messageKey : 'errors.server',
          }),
        ),
      ]);
      if (!vivant) return;
      setAgents(annuaire.liste);
      setErreurAgents(annuaire.erreur);
      setMatrice(grille.vue);
      setErreurMatrice(grille.erreur);
    })();
    return () => {
      vivant = false;
    };
  }, [client]);

  return (
    <section aria-label={t('usersRoles.landmark')} className="users-roles">
      <p className="users-roles__journal-notice">{t('usersRoles.journalNotice')}</p>

      <AgentsSection agents={agents} error={erreurAgents} />
      <MatrixSection error={erreurMatrice} matrix={matrice} />
    </section>
  );
}

/** L'annuaire des agents. */
function AgentsSection({
  agents,
  error,
}: {
  readonly agents: readonly BackOfficeAgent[] | null;
  readonly error: string | null;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <section aria-label={t('usersRoles.agentsTitle')} className="users-roles__agents">
      <h2 className="users-roles__section-title">{t('usersRoles.agentsTitle')}</h2>
      {error !== null ? (
        <p className="users-roles__notice" role="alert">
          {t(error)}
        </p>
      ) : agents === null ? (
        <p className="users-roles__idle">{t('usersRoles.loading')}</p>
      ) : agents.length === 0 ? (
        <SystemState
          bodyKey="usersRoles.agentsEmptyBody"
          testId="users-roles-agents-empty"
          titleKey="usersRoles.agentsEmptyTitle"
        />
      ) : (
        <>
          <p className="users-roles__meta" data-testid="users-roles-count">
            {t('usersRoles.agentsCount', { count: agents.length })}
          </p>
          <div className="users-roles__table-frame">
            <table className="users-roles__table" data-testid="users-roles-agents">
              <thead>
                <tr>
                  <th scope="col">{t('usersRoles.columns.agent')}</th>
                  <th scope="col">{t('usersRoles.columns.role')}</th>
                  <th scope="col">{t('usersRoles.columns.status')}</th>
                  <th scope="col">{t('usersRoles.columns.access')}</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr data-testid={`agent-row-${agent.username}`} key={agent.id}>
                    <td>
                      <span className="users-roles__agent-name">{agent.displayName}</span>
                      <span className="users-roles__agent-login">{agent.username}</span>
                    </td>
                    <td>{agent.roleCode}</td>
                    <td>{agent.status}</td>
                    {/* **Un VERROU et un GEL ne se rendent pas pareil.** Le
                        premier est temporaire — trop de mots de passe faux, il
                        tombe tout seul — le second est une décision, qui ne se
                        lève que par une autre décision. Les confondre ferait
                        prendre un agent maladroit pour un agent écarté. */}
                    <td>
                      <AccessBadge agent={agent} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/** L'état d'accès d'un agent : gelé, verrouillé, ou ouvert. */
function AccessBadge({ agent }: { readonly agent: BackOfficeAgent }): ReactNode {
  const { t } = useTranslation();
  if (agent.frozenAt !== null) {
    return (
      <span className="users-roles__access" data-tone="danger">
        {t('usersRoles.access.frozen', { at: agent.frozenAt })}
      </span>
    );
  }
  if (agent.lockedUntil !== null) {
    return (
      <span className="users-roles__access" data-tone="warning">
        {t('usersRoles.access.locked', { until: agent.lockedUntil })}
      </span>
    );
  }
  // Les tentatives manquées se disent même sans verrou : c'est ce qui précède
  // un verrou, et un administrateur veut le voir venir.
  return (
    <span className="users-roles__access" data-tone="success">
      {agent.failedAttempts > 0
        ? t('usersRoles.access.openWithFailures', { count: agent.failedAttempts })
        : t('usersRoles.access.open')}
    </span>
  );
}

/** La matrice, en lecture, groupée par module. */
function MatrixSection({
  error,
  matrix,
}: {
  readonly error: string | null;
  readonly matrix: PermissionMatrix | null;
}): ReactNode {
  const { t } = useTranslation();

  /** Les cellules, indexées par rôle et action — une seule passe. */
  const cellules = useMemo(() => {
    const table = new Map<string, string>();
    for (const cellule of matrix?.cells ?? []) {
      table.set(`${cellule.roleCode}/${cellule.actionCode}`, cellule.grantKind);
    }
    return table;
  }, [matrix]);

  /** Les actions, groupées par module, dans l'ordre servi. */
  const parModule = useMemo(() => {
    const groupes = new Map<string, MatrixAction[]>();
    for (const action of matrix?.actions ?? []) {
      const existant = groupes.get(action.module) ?? [];
      existant.push(action);
      groupes.set(action.module, existant);
    }
    return [...groupes.entries()];
  }, [matrix]);

  if (error !== null) {
    return (
      <section aria-label={t('usersRoles.matrixTitle')} className="users-roles__matrix">
        <h2 className="users-roles__section-title">{t('usersRoles.matrixTitle')}</h2>
        <p className="users-roles__notice" role="alert">
          {t(error)}
        </p>
      </section>
    );
  }
  if (matrix === null) {
    return (
      <section aria-label={t('usersRoles.matrixTitle')} className="users-roles__matrix">
        <h2 className="users-roles__section-title">{t('usersRoles.matrixTitle')}</h2>
        <p className="users-roles__idle">{t('usersRoles.loading')}</p>
      </section>
    );
  }

  return (
    <section aria-label={t('usersRoles.matrixTitle')} className="users-roles__matrix">
      <h2 className="users-roles__section-title">{t('usersRoles.matrixTitle')}</h2>
      <p className="users-roles__meta">{t('usersRoles.matrixBody')}</p>
      {/* Ce que l'écran NE FAIT PAS, et pourquoi. Une omission tue est une
          omission que la revue suivante refera. */}
      <p className="users-roles__meta">{t('usersRoles.matrixReadOnly')}</p>

      <div className="users-roles__table-frame users-roles__table-frame--matrix">
        <table className="users-roles__table users-roles__table--matrix"
               data-testid="users-roles-matrix">
          <thead>
            <tr>
              <th scope="col">{t('usersRoles.columns.action')}</th>
              {matrix.roles.map((role) => (
                <th key={role.code} scope="col">
                  {role.code}
                </th>
              ))}
            </tr>
          </thead>
          {parModule.map(([module, actions]) => (
            <tbody key={module}>
              <tr className="users-roles__module-row">
                <th colSpan={matrix.roles.length + 1} scope="colgroup">
                  {module}
                </th>
              </tr>
              {actions.map((action) => (
                <tr data-testid={`matrix-row-${action.code}`} key={action.code}>
                  <th className="users-roles__action-cell" scope="row">
                    <span className="users-roles__action-code">{action.code}</span>
                    <span className="users-roles__action-label">{action.label}</span>
                  </th>
                  {matrix.roles.map((role) => (
                    <Cell
                      grant={cellules.get(`${role.code}/${action.code}`) ?? 'FORBIDDEN'}
                      key={role.code}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}

/**
 * Une cellule de la matrice.
 *
 * **Le glyphe est décoratif, le mot porte l'information.** « ✔✔ » a un sens
 * réglementaire — deux signatures —, et un auditeur qui distingue mal deux
 * nuances ne doit pas avoir à le deviner.
 */
function Cell({ grant }: { readonly grant: string }): ReactNode {
  const { t } = useTranslation();
  const glyphe = grant === 'ALLOWED' ? '✓' : grant === 'DUAL_APPROVAL' ? '✓✓' : '–';
  const cle =
    grant === 'ALLOWED'
      ? 'usersRoles.grants.allowed'
      : grant === 'DUAL_APPROVAL'
        ? 'usersRoles.grants.dual'
        : 'usersRoles.grants.forbidden';
  return (
    <td className="users-roles__cell" data-grant={grant}>
      <span aria-hidden="true">{glyphe}</span>
      <span className="shell__reading">{t(cle)}</span>
    </td>
  );
}

/** Un état de zone. Voir `ReportingScreen` pour ce que trois `<p>` gris coûtent. */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="users-roles__state" data-testid={testId}>
      <span aria-hidden="true" className="users-roles__state-emblem" />
      <p className="users-roles__state-title">{t(titleKey)}</p>
      <p className="users-roles__state-body">{t(bodyKey)}</p>
    </div>
  );
}
